/* eslint-disable */
import React, { FC, useEffect } from 'react';
import { useLocation } from 'react-router';
import { Spin } from 'antd';
import styled from 'styled-components';
import colors from '~/constants/colors';
import { useGetApplicationNavSteps } from '~/controllers/navigation';
import { useDirection } from '~/hooks/i18nHooks';
import ApplicationNavStep from './ApplicationNavStep';
import ChildNavStep from './ChildNavStep';
import { useGetApplication } from '~/controllers/wizard';
import { isItDisabled, isItDone } from '~/utils/navigation';
import { getStagePayload } from '~/utils/getStagePayload';
import { useReduxState } from '~/hooks';
import { useTranslation } from 'react-i18next';

const ApplicationNavigation: FC<{ activeSubStepIndex?: number }> = ({ activeSubStepIndex }) => {
  const { data, navStepsQuery, stagePayload } = useGetApplicationNavSteps();
  const { t } = useTranslation('fillApplication');
  const [draftKey, ...rest] = useReduxState<any>('draftKey', null);
  const dir = useDirection();
  const location = useLocation();

  const activeStep = stagePayload?.activeStep;

  return (
    <Container className="ApplicationNavigation" dir={dir}>
      {navStepsQuery?.steps?.map((item: any, index: any, arr: any) => {
        const isLast = index === arr.length - 1;
        const hasSteps = item.steps?.length > 0;
        return (
          <>
            <div key={item.stepKey} className="ApplicationNavigation-StepContainer">
              <ApplicationNavStep
                index={index + 1}
                {...item}
                stepKey={item.key}
                disabled={isItDisabled({ item, applicationData: data, stagePayload, hasSteps })}
                done={isItDone({ item, applicationData: data, stagePayload, draftKey })}
              />
              {!isLast && (
                <CustomDevider
                  dir={dir}
                  done={isItDone({ item, applicationData: data, stagePayload })}
                  disabled={isItDisabled({
                    item,
                    applicationData: data,
                    stagePayload,
                    hasSteps,
                    draftKey,
                  })}
                  active={item?.active ?? location.pathname === item?.route}
                />
              )}
            </div>
            {(() => {
              // Show contract sub-steps when we're on the agreement route OR when activeSubStepIndex is provided
              const isContractItem = item?.key === 'contract';
              const onContractRoute = location.pathname.includes('/ksa/agreement');
              const shouldExpand = hasSteps && (
                isContractItem
                  ? (activeSubStepIndex !== undefined || onContractRoute || activeStep?.key.includes(item?.key))
                  : activeStep?.key.includes(item?.key)
              );
              if (!shouldExpand) return null;
              return item?.steps?.map((subItem: any, subIndex: any, subArr: any) => {
                const hasStepsChild = item.steps?.length > 0;
                const isSubItemActive =
                  activeSubStepIndex !== undefined
                    ? subIndex === activeSubStepIndex
                    : (subItem?.active ?? location.pathname === subItem?.route);

                // For contract sub-steps: keep in-progress steps/connectors BLUE (active), and only
                // mark them done (green) once ALL sub-steps are complete (activeSubStepIndex has moved
                // past the last sub-step). Moving Agreement -> Identity Verification must NOT green Agreement.
                const contractDone =
                  activeSubStepIndex !== undefined && isContractItem
                    ? activeSubStepIndex >= subArr.length
                    : undefined;
                const contractDisabled =
                  activeSubStepIndex !== undefined && isContractItem
                    ? subIndex > activeSubStepIndex
                    : undefined;

                return (
                  <div key={`${subItem.key}-${subIndex}`} className="ApplicationNavigation-StepContainer">
                    <ChildNavStep
                      dir={dir}
                      index={subIndex + 1}
                      {...subItem}
                      stepKey={subItem.key}
                      disabled={contractDisabled !== undefined ? contractDisabled : isItDisabled({
                        item: subItem,
                        applicationData: data,
                        stagePayload,
                        hasSteps: false,
                        previousStep: subArr[subIndex - 1],
                      })}
                      done={contractDone !== undefined ? contractDone : isItDone({
                        item: subItem,
                        applicationData: data,
                        stagePayload,
                        draftKey,
                        child: true,
                        parentStatus: item?.validationState,
                      })}
                      active={isSubItemActive}
                      hasSteps={hasStepsChild}
                    />
                    <CustomDevider
                      dir={dir}
                      done={contractDone !== undefined ? contractDone : isItDone({
                        item: subItem,
                        applicationData: data,
                        stagePayload,
                        draftKey,
                        child: true,
                        parentStatus: item?.validationState,
                      })}
                      disabled={contractDisabled !== undefined ? contractDisabled : isItDisabled({
                        item: subItem,
                        applicationData: data,
                        stagePayload,
                        hasSteps: false,
                      })}
                      active={isSubItemActive}
                    />
                  </div>
                );
              });
            })()}
          </>
        );
      })}
    </Container>
  );
};

const CustomDevider = styled.div<any>`
  display: flex;
  border-left: 1px solid ${colors.borderColorBase};
  border-color: ${({ done, disabled, active }) =>
    done ? colors.doneColor : disabled ? colors.disabledBlueTextColor : colors.blue[1100]};
  height: 36px;
  width: ${({ dir }) => (dir === 'rtl' ? '10px' : '1px')};

  margin-left: 11.5px;
`;

const Container = styled.div<any>`
  position: sticky;
  margin-right: ${({ dir }) => (dir === 'rtl' ? '30%' : 'unset')};
  margin-left: ${({ dir }) => (dir === 'rtl' ? 'unset' : '30%')};
  //top: 66px;
  //margin-left: 30%;
  display: flex;
  flex-direction: column;
  .ApplicationNavigation-StepContainer {
    display: flex;
    flex-direction: column;
    .ApplicationNavigation-Divider {
      display: flex;
      border-left: 1px solid ${colors.borderColorBase};
      height: 36px;
      width: 1px;
      margin-left: 11.5px;
    }
  }
`;

export default ApplicationNavigation;
